import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import client from "../../../api/client";
import { dueLabel, fmtDay, fmtDayTime } from "../home/time";
import { errorMessage, getPlan } from "./api";
import Icon from "./Icon";
import LessonView, { AssignmentView } from "./LessonView";
import QuizView from "./QuizView";
import { CertificateProgress, HowGraded, WorkTable } from "./GradeBreakdown";
import PointsRules from "./PointsRules";
import { fmt, summarize } from "./progress";
import ProjectView from "./ProjectView";
import "./lms.css";

/*
 * Student course page, built with the approved prototype's markup and classes (learn/lms.css).
 * Screens are URL-driven so Back works: ?tab=, ?lesson=, ?assignment=<lessonId>, ?quiz=, ?project=.
 */

const THUMBS = ["ds", "ai", "da", "py"];
const st = (cls, text) => <span className={`st ${cls}`}>{text}</span>;

const lessonTag = (l) =>
  l.locked ? st("locked", fmtDay(l.start_time))
  : l.tracked === false && l.state === "recording" ? st("open", "Recording") // orientation: nothing to complete
  : l.complete ? st("done", "Done")
  : l.state === "live" ? st("today", "Live now")
  : l.state === "upcoming" ? (dueLabel(l.start_time) === "Today" ? st("today", "Today") : st("open", fmtDay(l.start_time)))
  : l.state === "pending" ? st("open", "Recording soon")
  : st("due", l.watch_percent ? `Watched ${l.watch_percent}%` : "Watch");

const quizTag = (q) =>
  q.passed ? st("done", `${Math.round(q.best_score)}% · passed`)
  : q.attempts_used ? st("due", `Best ${Math.round(q.best_score)}%`)
  : q.window === "upcoming" ? st("due", `Opens ${dueLabel(q.opens_at)}`)
  : q.window === "closed" ? st("open", "Closed")
  : st("due", q.closes_at ? `Due ${dueLabel(q.closes_at)}` : "Open");

const workTag = (sub, due) =>
  sub ? st("done", sub.grade ? sub.grade : "Submitted") : st("due", due ? `Due ${dueLabel(due)}` : "Not submitted");

function Witem({ icon, ico, title, sub, tag, onClick, nested }) {
  const inner = (<><span className={`ico ${ico}`}><Icon name={icon} /></span><span><strong>{title}</strong>{sub && <small>{sub}</small>}</span>{tag || <span />}</>);
  return onClick
    ? <button className={`witem ${nested ? "sub" : ""}`} type="button" onClick={onClick}>{inner}</button>
    : <div className={`witem ${nested ? "sub" : ""}`}>{inner}</div>;
}

function Week({ week, open: initiallyOpen, go }) {
  const [open, setOpen] = useState(initiallyOpen);
  const isBreak = week.type === "break";
  const cls = [isBreak ? "break" : "", week.type === "orientation" ? "orient" : "", week.locked ? "locked" : "", week.complete ? "done" : "", week.state === "current" && !week.locked ? "current" : ""].join(" ");
  const num = week.type === "week" ? week.label.replace("Week ", "") : week.type === "capstone" ? "C" : null;
  const dates = `${fmtDay(week.starts_on)} – ${fmtDay(week.ends_on)}`;
  const status = isBreak ? st("locked", "Break")
    : week.locked ? st("locked", "Opens later")
    : week.complete ? st("done", "Complete")
    : week.state === "current" ? st("today", "This week")
    : st("due", "Incomplete");

  if (isBreak) {
    return (
      <div className={`week ${cls}`}>
        <div className="week-btn"><span className="wnum"><Icon name="pause" /></span><span><strong>{week.title || "Break"}</strong><small>{dates} · no classes this week</small></span>{status}<span /></div>
      </div>
    );
  }
  const numbered = week.type === "week";
  return (
    <div className={`week ${cls}`} data-open={String(open && !week.locked)}>
      <button className="week-btn" type="button" disabled={week.locked} onClick={() => setOpen((o) => !o)} aria-expanded={open && !week.locked}>
        <span className="wnum">{week.complete ? "✓" : num ?? <Icon name="play" />}</span>
        <span><strong>{week.label}{week.title && week.title !== week.label ? ` · ${week.title}` : ""}</strong><small>{week.locked ? `Opens ${fmtDay(week.starts_on)}` : dates}{week.type === "orientation" ? " · not graded" : ""}</small></span>
        {status}
        <span className="chev">{week.locked ? <Icon name="lock" size={16} /> : <Icon name="chev" />}</span>
      </button>
      {!week.locked && (
        <div className="week-body">
          {/* numbered: only teaching weeks call their classes "Lesson 1, 2…" */}
          {week.summary && <p className="course-tag" style={{ padding: "10px 0 4px" }}>{week.summary}</p>}
          {week.lessons.map((l, i) => (
            <div key={l.id}>
              <Witem icon={l.state === "recording" ? "play" : "video"} ico={l.state === "recording" ? "rec" : "live"}
                title={numbered ? `Lesson ${i + 1} · ${l.title}` : l.title}
                sub={l.state === "recording" ? "Recording" : l.state === "pending" ? "Class ended · recording not posted yet" : `Live class · ${fmtDayTime(l.start_time)}`}
                tag={lessonTag(l)} onClick={() => go({ lesson: l.id })} />
              {!l.locked && l.materials?.length > 0 && (
                <Witem nested icon="files" ico="doc" title={`Materials · ${l.materials.map((m) => m.name).join(" · ")}`}
                  sub={numbered ? `From Lesson ${i + 1}` : `From ${l.title}`} tag={st("open", `${l.materials.length} ${l.materials.length === 1 ? "file" : "files"}`)} onClick={() => go({ lesson: l.id })} />
              )}
              {l.assignment && (
                <Witem nested icon="task" ico="task" title={`Assignment · ${l.assignment.title}`}
                  sub={`Given in ${numbered ? `Lesson ${i + 1}` : l.title}${l.assignment.due_at ? ` · due ${fmtDay(l.assignment.due_at)}` : ""}`}
                  tag={workTag(l.assignment.submission, l.assignment.due_at)} onClick={() => go({ assignment: l.id })} />
              )}
            </div>
          ))}
          {week.materials.length > 0 && (
            <Witem icon="files" ico="doc" title={`${week.label} materials`} sub={week.materials.map((m) => m.name).join(" · ")}
              tag={st("open", `${week.materials.length} ${week.materials.length === 1 ? "file" : "files"}`)} onClick={() => go({ materials: week.position })} />
          )}
          {week.quizzes.map((q) => (
            <Witem key={q.uuid} icon="assess" ico="assess" title={q.title} sub={`Timed · ${q.time_limit_minutes} min · ${q.questions_count} questions`} tag={quizTag(q)} onClick={() => go({ quiz: q.uuid })} />
          ))}
          {week.projects.map((p) => (
            <Witem key={p.id} icon="folder" ico="live" title={p.title} sub="Project" tag={workTag(p.submission, p.due_at)} onClick={() => go({ project: p.id })} />
          ))}
          {!week.lessons.length && !week.materials.length && !week.quizzes.length && !week.projects.length && <p className="course-tag" style={{ padding: "12px 0" }}>Nothing has been added to this week yet.</p>}
        </div>
      )}
    </div>
  );
}

function MaterialsView({ week }) {
  return (
    <>
      <div className="detail-h"><span className="kicker">{week.label}</span><h2>{week.label} materials</h2><p>Files your instructor shared for this week. Open them here or download a copy.</p></div>
      <div className="plain">
        {week.materials.map((m) => (
          <div key={m.id} className="file">
            <span className="ico doc"><Icon name="files" /></span>
            <div><strong>{m.name}</strong><small>Week material</small></div>
            <span style={{ display: "flex", gap: 4 }}><a href={m.url} target="_blank" rel="noopener noreferrer" aria-label={`Open ${m.name}`}><Icon name="eye" /></a></span>
          </div>
        ))}
      </div>
    </>
  );
}

export function Grades({ plan, go }) {
  const g = summarize(plan);
  const att = g.attendance;
  return (
    <>
      <div className="gsum">
        <div><b>{fmt(att.percent)}</b><span>Attendance · {att.attended} of {att.held} classes</span></div>
        <div><b>{g.activities.score == null ? "—" : g.activities.score}</b><span>Activities score{g.ended ? "" : " so far"} · need {g.criteria.activities_pass}</span></div>
        <div><b>{g.submitted} / {g.workTotal}</b><span>Assignments submitted</span></div>
      </div>
      <section className="card">
        <div className="card-h"><h3>Toward your certificate</h3>{g.ready && st("done", "All requirements met")}</div>
        <CertificateProgress g={g} />
      </section>
      <WorkTable g={g} go={go} />
      <HowGraded criteria={g.criteria} />
    </>
  );
}

function Board({ plan }) {
  const [data, setData] = useState(null);
  // This course's students only (each course has its own board).
  useEffect(() => { client.get(`/me/courses/${plan.course.uuid}/leaderboard`, { params: { cohort_id: plan.cohort.id } }).then((r) => setData(r.data)).catch(() => setData({})); }, [plan.course.uuid, plan.cohort.id]);
  const entries = data?.leaderboard?.entries ?? [];
  const me = entries.find((e) => e.is_me);
  return (
    <>
      <div className="rule"><Icon name="info" /><span><strong>Keep your streak:</strong> do at least one thing each day: answer the daily challenge, attend a live class, finish a class recording, or submit a quiz, assignment or project. Miss a day and your streak starts again.</span></div>
      <div className="gsum">
        <div><b>{me ? `#${me.rank}` : "—"}</b><span>Your rank in {plan.course.title}</span></div>
        <div><b>{data?.points ?? "—"}</b><span>Your points</span></div>
        <div><b>{data?.streak?.current ?? "—"}</b><span>Day streak</span></div>
      </div>
      <div className="plain">
        {!data ? <p className="course-tag" style={{ padding: "12px 0" }}>Loading…</p> : entries.length ? entries.map((e, i) => e.gap
          ? <div key={`g${i}`} className="lb-row"><span className="n">…</span><span className="who" /><span className="pts" /></div>
          : <div key={`${e.rank}${e.name}`} className={`lb-row ${e.is_me ? "me" : ""}`}><span className="n">{e.rank}</span><span className="who">{e.is_me ? "You" : e.name}</span><span className="pts">{e.points} pts</span></div>)
          : <p className="course-tag" style={{ padding: "12px 0" }}>The leaderboard fills up as your cohort earns points.</p>}
      </div>
      <PointsRules rules={data?.rules} breakdown={data?.breakdown} />
    </>
  );
}

export default function StudentCoursePage() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);

  // Which cohort's run of this course (a student can take the same course in two cohorts).
  const cohortId = params.get("cohort_id");
  const load = () => getPlan(uuid, cohortId).then((p) => { setPlan(p); setError(null); }).catch((e) => setError(errorMessage(e, "We couldn't load this course.")));
  useEffect(() => { setPlan(null); load(); }, [uuid, cohortId]); // eslint-disable-line react-hooks/exhaustive-deps

  const lessons = useMemo(() => (plan ? [
    // Only teaching weeks number their lessons; orientation, capstone and special weeks just use class titles.
    ...plan.weeks.flatMap((w) => w.lessons.filter((l) => !l.locked).map((l, i) => ({ ...l, weekLabel: w.label, number: w.type === "week" ? i + 1 : null }))),
    ...plan.other_lessons.map((l) => ({ ...l, weekLabel: "Other classes", number: null })),
  ] : []), [plan]);

  const tab = params.get("tab") || "weeks";
  const lessonId = Number(params.get("lesson")) || null;
  const assignmentOf = Number(params.get("assignment")) || null;
  const quizId = params.get("quiz");
  const projectId = Number(params.get("project")) || null;
  const materialsOf = Number(params.get("materials")) || null;
  const go = (p) => { setParams(cohortId ? { ...p, cohort_id: cohortId } : p); window.scrollTo({ top: 0 }); };
  const inCourse = !!(lessonId || assignmentOf || quizId || projectId || materialsOf);

  // Coming back to the course view: refresh progress, submissions and scores.
  useEffect(() => { if (!inCourse && plan) load(); }, [inCourse]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return <div className="lms"><div className="main single"><div className="view">
      <section className="complete-box"><div><strong>{error}</strong><small>Go back to Home and open the course again.</small></div><button className="cbtn quiet" type="button" onClick={() => navigate("/dashboard")}>Back to Home</button></section>
    </div></div></div>;
  }
  if (!plan) return <div className="lms"><div className="main single"><div className="view"><div className="skeleton" style={{ height: 200 }} /><div className="skeleton" style={{ height: 64 }} /><div className="skeleton" style={{ height: 64 }} /></div></div></div>;

  const findLesson = (id) => { const i = lessons.findIndex((l) => l.id === id); return [i, i >= 0 ? lessons[i] : null]; };
  const [lessonIdx, lesson] = findLesson(lessonId);
  const [, assignmentLesson] = findLesson(assignmentOf);
  const quiz = quizId ? plan.weeks.flatMap((w) => w.quizzes.map((q) => ({ ...q, weekLabel: w.label }))).find((q) => q.uuid === quizId) : null;
  const project = projectId ? plan.weeks.flatMap((w) => w.projects.map((p) => ({ ...p, weekLabel: w.label }))).find((p) => p.id === projectId) : null;
  const materialsWeek = materialsOf ? plan.weeks.find((w) => w.position === materialsOf && !w.locked) : null;

  // Trail: Home › Course › Week › Item
  const course = { label: plan.course.title, to: tab !== "weeks" ? { tab } : {} };
  const trail = [{ label: "Home", home: true }, course];
  const lessonLabel = (l) => (l.number ? `Lesson ${l.number}` : l.title);
  if (lesson) trail.push({ label: lesson.weekLabel, to: {} }, { label: lessonLabel(lesson) });
  if (assignmentLesson) trail.push({ label: assignmentLesson.weekLabel, to: {} }, { label: lessonLabel(assignmentLesson), to: { lesson: assignmentLesson.id } }, { label: "Assignment" });
  if (quiz) trail.push({ label: quiz.weekLabel, to: {} }, { label: "Quiz" });
  if (project) trail.push({ label: project.weekLabel, to: {} }, { label: "Project" });
  if (materialsWeek) trail.push({ label: materialsWeek.label, to: {} }, { label: "Materials" });
  if (!inCourse) trail[1] = { label: plan.course.title };

  const next = plan.next_lesson;
  const nextOpen = next && lessons.some((l) => l.id === next.id);
  const currentWeek = plan.weeks.find((w) => w.state === "current" && !w.locked);
  const thumb = THUMBS[(plan.course.id || 0) % THUMBS.length];

  return (
    <div className="lms">
      <div className="main single">
        <div className="pagebar">
          {inCourse && <button className="back-btn" type="button" onClick={() => navigate(-1)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m15 6-6 6 6 6" /></svg>
            {trail.length > 3 && trail[trail.length - 2].to?.lesson ? "Back" : "Back to course"}
          </button>}
          <nav className="trail" aria-label="You are here">
            {trail.map((p, i) => {
              const last = i === trail.length - 1;
              return (
                <span key={`${p.label}${i}`} style={{ display: "contents" }}>
                  {last ? <span className="here">{p.label}</span>
                    : p.home ? <button type="button" onClick={() => navigate("/dashboard")}>{p.label}</button>
                    : p.to ? <button type="button" onClick={() => go(p.to)}>{p.label}</button> : <span>{p.label}</span>}
                  {!last && <span className="sep">›</span>}
                </span>
              );
            })}
          </nav>
        </div>

        <div className="view">
          {lesson && <LessonView lesson={lesson} prev={lessons[lessonIdx - 1]} next={lessons[lessonIdx + 1]}
            onOpen={(l) => go({ lesson: l.id })} onAssignment={() => go({ assignment: lesson.id })} onChanged={load} />}
          {assignmentLesson?.assignment && <AssignmentView lesson={assignmentLesson} onLesson={() => go({ lesson: assignmentLesson.id })} onChanged={load} />}
          {quiz && <QuizView quiz={quiz} onDone={load} />}
          {project && <ProjectView project={project} course={plan.course.title} onChanged={load} />}
          {materialsWeek && <MaterialsView week={materialsWeek} />}
          {inCourse && !lesson && !assignmentLesson?.assignment && !quiz && !project && !materialsWeek && (
            <section className="complete-box"><div><strong>This isn't available yet</strong><small>It opens when its week starts.</small></div><button className="cbtn quiet" type="button" onClick={() => go({})}>Back to course</button></section>
          )}

          {!inCourse && (
            <>
              <section className="course-hero">
                <div className={`thumb ${thumb} ${plan.course.image_url ? "has-img" : ""}`}>
                  {plan.course.image_url && <img src={plan.course.image_url} alt="" />}
                  <span>{plan.course.title}</span>
                </div>
                <div className="info">
                  <h2>{plan.course.title}</h2>
                  <span className="sub">{plan.cohort.name}{plan.week ? ` · ${plan.week.label}${plan.week.number ? ` of ${plan.week.of}` : ""}` : ""}{plan.attendance.percent != null ? ` · ${plan.attendance.percent}% attendance` : ""}</span>
                  <div className="prow"><div className="pbar"><i style={{ width: `${plan.progress}%` }} /></div><b>{plan.progress}%</b></div>
                  {plan.status === "active" && next && (
                    <div className="continue">
                      <div><small>Up next</small><strong>{next.week_label ? `${next.week_label} · ` : ""}{next.title} · {fmtDayTime(next.start_time)}</strong></div>
                      {nextOpen && <button className="cbtn go" type="button" onClick={() => go({ lesson: next.id })}>{next.state === "live" ? "Join class" : "Go to class"}</button>}
                    </div>
                  )}
                  {plan.status !== "active" && (
                    <div className="cert-note"><Icon name="clock" size={14} /><span>{plan.status === "completed" ? "You've completed this course. Your lessons stay here to review." : "Classes have ended. Your certificate is issued once you meet the requirements and your capstone is marked."}</span></div>
                  )}
                </div>
              </section>

              <nav className="tabs" role="tablist">
                {[["weeks", "Weeks"], ["activities", "Activities"], ["grades", "Grades"], ["board", "Leaderboard"]].map(([k, l]) => (
                  <button key={k} className="tab" type="button" role="tab" aria-selected={tab === k} onClick={() => go(k === "weeks" ? {} : { tab: k })}>{l}</button>
                ))}
              </nav>

              {tab === "weeks" && (
                <>
                  <div className="rule"><Icon name="info" /><span>Each week opens on its start date. A lesson is complete when you attend it live, or watch at least 60% of its recording (that also marks your attendance). Your certificate needs 70% attendance, an activities score of 70 or more (quizzes count 40%, assignments 60%) and a capstone graded A, B or C. The Grades tab shows how each is worked out.</span></div>
                  <div className="weeks-list">
                    {plan.weeks.map((w) => <Week key={`${w.position}-${w.id}`} week={w} open={w === currentWeek} go={go} />)}
                  </div>
                  {plan.other_lessons.length > 0 && (
                    <div className="plain">
                      {lessons.filter((l) => l.weekLabel === "Other classes").map((l) => (
                        <Witem key={l.id} icon="video" ico="live" title={l.title} sub={fmtDayTime(l.start_time)} tag={lessonTag(l)} onClick={() => go({ lesson: l.id })} />
                      ))}
                    </div>
                  )}
                  {!plan.weeks.length && !plan.other_lessons.length && <section className="complete-box"><div><strong>Your classes haven't been scheduled yet</strong><small>They'll appear here week by week.</small></div></section>}
                </>
              )}

              {tab === "activities" && (() => {
                const open = plan.weeks.filter((w) => !w.locked);
                const items = [
                  ...open.flatMap((w) => w.lessons.map((l, i) => l.assignment && (
                    <Witem key={`a${l.assignment.id}`} icon="task" ico="task" title={l.assignment.title} sub={`Assignment · given in ${w.label}${w.type === "week" ? `, Lesson ${i + 1}` : ""}`}
                      tag={workTag(l.assignment.submission, l.assignment.due_at)} onClick={() => go({ assignment: l.id })} />))).filter(Boolean),
                  ...open.flatMap((w) => w.quizzes.map((q) => (
                    <Witem key={q.uuid} icon="assess" ico="assess" title={q.title} sub={`Quiz · ${w.label} · ${q.time_limit_minutes} min`} tag={quizTag(q)} onClick={() => go({ quiz: q.uuid })} />))),
                  ...open.flatMap((w) => w.projects.map((p) => (
                    <Witem key={`p${p.id}`} icon="folder" ico="live" title={p.title} sub={`Project · ${w.label}`} tag={workTag(p.submission, p.due_at)} onClick={() => go({ project: p.id })} />))),
                ];
                return items.length ? <div className="plain">{items}</div>
                  : <section className="complete-box"><div><strong>No activities yet</strong><small>Assignments, quizzes and projects appear here as your instructors add them.</small></div></section>;
              })()}

              {tab === "grades" && <Grades plan={plan} go={go} />}
              {tab === "board" && <Board plan={plan} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
